import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { documentsAPI, SITE_NAMES, uploadAPI } from '../../lib/api';
import { formatDate, CATEGORY_LABELS } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Plus, ArrowLeft, Pencil, Trash2, MoreVertical, FileText, Download, Upload } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';

export const DocumentsListAdmin = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const response = await documentsAPI.getAll(null, false);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Nepodařilo se načíst dokumenty');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await documentsAPI.delete(deleteId);
      setDocuments(documents.filter(d => d.id !== deleteId));
      toast.success('Dokument byl smazán');
    } catch (error) {
      toast.error('Nepodařilo se smazat dokument');
    } finally {
      setDeleteId(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">Dokumenty</h1>
          <p className="text-[#64748B]">Správa dokumentů a souborů ke stažení</p>
        </div>
        <Link to="/admin/dokumenty/novy">
          <Button className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="add-document">
            <Plus size={18} className="mr-2" />
            Nový dokument
          </Button>
        </Link>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Název</th>
              <th className="hidden md:table-cell">Kategorie</th>
              <th className="hidden md:table-cell">Datum</th>
              <th>Stav</th>
              <th className="w-20">Akce</th>
            </tr>
          </thead>
          <tbody>
            {documents.length > 0 ? documents.map((doc) => (
              <tr key={doc.id} data-testid={`document-row-${doc.id}`}>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#FF6B6B]/10 flex items-center justify-center flex-shrink-0">
                      <FileText size={18} className="text-[#FF6B6B]" />
                    </div>
                    <div>
                      <div className="font-medium text-[#2C3E50]">{doc.title}</div>
                      <div className="text-xs text-[#64748B] md:hidden">{CATEGORY_LABELS[doc.category]}</div>
                    </div>
                  </div>
                </td>
                <td className="hidden md:table-cell text-[#64748B]">{CATEGORY_LABELS[doc.category]}</td>
                <td className="hidden md:table-cell text-[#64748B]">{formatDate(doc.created_at)}</td>
                <td>
                  <span className={`badge ${doc.is_published ? 'badge-success' : 'badge-warning'}`}>
                    {doc.is_published ? 'Publikováno' : 'Skryto'}
                  </span>
                </td>
                <td>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreVertical size={16} /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                          <Download size={14} className="mr-2" />Stáhnout
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`/admin/dokumenty/${doc.id}`} className="cursor-pointer">
                          <Pencil size={14} className="mr-2" />Upravit
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteId(doc.id)} className="cursor-pointer text-red-600">
                        <Trash2 size={14} className="mr-2" />Smazat
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={5} className="text-center py-8 text-[#64748B]">Žádné dokumenty</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Smazat dokument?</AlertDialogTitle>
            <AlertDialogDescription>Tato akce je nevratná.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušit</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Smazat</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const DocumentFormAdmin = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file_url: '',
    category: 'uredni_deska',
    is_published: true,
    site: 'ms'
  });

  useEffect(() => {
    if (isEdit) loadDocument();
  }, [id]);

  const loadDocument = async () => {
    try {
      const response = await documentsAPI.getAll();
      const doc = response.data.find(d => d.id === id);
      if (doc) setFormData(doc);
      else throw new Error('Not found');
    } catch (error) {
      toast.error('Nepodařilo se načíst dokument');
      navigate('/admin/dokumenty');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await documentsAPI.update(id, formData);
        toast.success('Dokument byl aktualizován');
      } else {
        await documentsAPI.create(formData);
        toast.success('Dokument byl vytvořen');
      }
      navigate('/admin/dokumenty');
    } catch (error) {
      toast.error('Nepodařilo se uložit dokument');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admin/dokumenty" className="text-[#64748B] hover:text-[#4ECDC4]"><ArrowLeft size={24} /></Link>
        <h1 className="text-2xl font-bold text-[#2C3E50]">{isEdit ? 'Upravit dokument' : 'Nový dokument'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
        <div className="space-y-2">
          <Label htmlFor="title">Název *</Label>
          <Input id="title" name="title" value={formData.title} onChange={handleChange} required className="rounded-xl" data-testid="document-title" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Popis</Label>
          <Textarea id="description" name="description" value={formData.description || ''} onChange={handleChange} rows={3} className="rounded-xl resize-none" data-testid="document-description" />
        </div>

        <div className="space-y-2">
          <Label>Soubor (PDF, DOC, ...) *</Label>
          <div className="flex gap-2 items-center">
            {formData.file_url && (
              <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg text-sm text-green-700 flex-shrink-0">
                <FileText size={16} />
                Soubor nahrán
              </div>
            )}
            <label className="cursor-pointer flex-1">
              <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.odt,.ods,.rtf,image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0]; if (!file) return;
                setUploading(true);
                try { const res = await uploadAPI.upload(file); setFormData(prev => ({ ...prev, file_url: res.data.url })); toast.success('Soubor nahrán'); }
                catch (err) { toast.error('Nepodarilo se nahrát soubor'); } finally { setUploading(false); }
              }} />
              <Button type="button" variant="outline" className="w-full rounded-xl" disabled={uploading} asChild>
                <span><Upload size={16} className="mr-2" />{uploading ? 'Nahrávání...' : formData.file_url ? 'Zmenit soubor' : 'Nahrát soubor'}</span>
              </Button>
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="category">Kategorie *</Label>
          <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
            <SelectTrigger className="rounded-xl" data-testid="document-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="uredni_deska">Úřední deska</SelectItem>
              <SelectItem value="pro_rodice">Pro rodiče</SelectItem>
              <SelectItem value="formulare">Formuláře</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Sekce</Label>
          <Select value={formData.site || 'ms'} onValueChange={(value) => setFormData(prev => ({ ...prev, site: value }))}>
            <SelectTrigger className="rounded-xl" data-testid="document-site-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ms">{SITE_NAMES.ms}</SelectItem>
              <SelectItem value="ds">{SITE_NAMES.ds}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
          <div>
            <div className="font-medium text-[#2C3E50]">Publikovat</div>
            <div className="text-sm text-[#64748B]">Zobrazit na webu</div>
          </div>
          <Switch checked={formData.is_published} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))} data-testid="document-publish-switch" />
        </div>

        <div className="flex gap-4 pt-4">
          <Button type="submit" disabled={saving} className="flex-1 rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="document-save">
            {saving ? 'Ukládání...' : (isEdit ? 'Uložit změny' : 'Vytvořit dokument')}
          </Button>
          <Link to="/admin/dokumenty" className="flex-1">
            <Button type="button" variant="outline" className="w-full rounded-full">Zrušit</Button>
          </Link>
        </div>
      </form>
    </div>
  );
};

export default DocumentsListAdmin;
